import React, { useMemo } from "react";

import { useTranslation } from "utils";

import { Flex, Icon, Typography } from "components";

import { PayoutBaselineBarChart } from "pages/app/field-timeline/components/payout-context-drawer/PayoutBaselineBarChart";
import { resolveBaselineSeries } from "pages/app/field-timeline/utils/PayoutUtils";

type PayoutContextBaselineCardProps = {
  baselineFootprints: Record<number, number>;
};

export const PayoutContextBaselineCard = ({ baselineFootprints }: PayoutContextBaselineCardProps) => {
  const { t } = useTranslation("fieldTimeline");

  const {
    years: baselineYears,
    values: baselineValues,
    average: baselineAverage,
  } = useMemo(() => resolveBaselineSeries(baselineFootprints), [baselineFootprints]);

  const positiveImpactTotal = baselineValues.filter((v) => v > 0).reduce((sum, v) => sum + v, 0);
  const negativeYearValue = Math.min(...baselineValues, 0);
  const yearlyImpactsLabel = baselineValues.map((v) => (v < 0 ? `(${v})` : `${v}`)).join(" + ");

  return (
    <Flex vertical className="pcd-card">
      <Flex vertical className="pcd-baseline-header" gap=".5rem">
        <Typography variant="h4">{t("impactPanel.payoutImpact.contextDrawer.baseline.title")}</Typography>
        <Typography variant="paragraph-m">
          {t("impactPanel.payoutImpact.contextDrawer.baseline.description", {
            startYear: baselineYears[0],
            endYear: baselineYears[baselineYears.length - 1],
          })}
        </Typography>
      </Flex>
      <div className="pcd-baseline-visual">
        <PayoutBaselineBarChart years={baselineYears} values={baselineValues} average={baselineAverage} />
      </div>
      <Flex className="pcd-baseline-legend" gap="1.5rem">
        <Flex align="center" gap="0.5rem">
          <i className="actual" />
          <Typography variant="paragraph-s" className="pcd-legend-copy">{`${t("impactPanel.payoutImpact.contextDrawer.baseline.positiveImpact")} (${Math.round(
            positiveImpactTotal
          )}t)`}</Typography>
        </Flex>
        <Flex align="center" gap="0.5rem">
          <i className="negative" />
          <Typography variant="paragraph-s" className="pcd-legend-copy">{`${t("impactPanel.payoutImpact.contextDrawer.baseline.negativeYear")} (${Math.round(
            negativeYearValue
          )}t)`}</Typography>
        </Flex>
        <Flex align="center" gap="0.5rem">
          <i className="average" />
          <Typography variant="paragraph-s" className="pcd-legend-copy">{`${t("impactPanel.payoutImpact.contextDrawer.baseline.average", {
            value: baselineAverage,
          })}`}</Typography>
        </Flex>
      </Flex>

      <Flex vertical className="pcd-breakdown pcd-breakdown-edge pcd-breakdown-calc">
        <Flex align="center" className="pcd-breakdown-header" gap="0.25rem">
          <Icon iconName="tabler/chevron-down" size="m-light" />
          <Typography variant="paragraph-m" bold>
            {t("impactPanel.payoutImpact.contextDrawer.baseline.breakdown")}
          </Typography>
        </Flex>
        <Flex vertical className="pcd-breakdown-body pcd-breakdown-body-calc" gap="0.5rem">
          <Flex justify="space-between" className="row">
            <Typography variant="paragraph-m">{t("impactPanel.payoutImpact.contextDrawer.baseline.yearlyImpacts")}</Typography>
            <Typography variant="paragraph-m">{`${yearlyImpactsLabel}t = ${Math.round(baselineValues.reduce((sum, value) => sum + value, 0))}t`}</Typography>
          </Flex>
          <Flex justify="space-between" className="row">
            <Typography variant="paragraph-m">
              {t("impactPanel.payoutImpact.contextDrawer.baseline.averageOfThreeYears", {
                count: baselineValues.length,
                years: baselineYears.join(", "),
              })}
            </Typography>
            <Typography variant="paragraph-m">{`${Math.round(baselineValues.reduce((sum, value) => sum + value, 0))}t / ${
              baselineValues.length
            } = ${baselineAverage}t`}</Typography>
          </Flex>
        </Flex>
        <Flex justify="flex-end" className="pcd-breakdown-total margin-top-100">
          <Typography variant="h1">{`${baselineAverage}t CO₂`}</Typography>
        </Flex>
      </Flex>
    </Flex>
  );
};
